import type { EditorState } from '../../vendor/src/features/remotion-editor/editor/state/types';
import { editorValueSignature, type NativeWorkScope } from '../../protocol/native-envelope';
import { readSceneGraph, resolveSourceSceneSpecOwner, validateSceneSpecTarget, type SceneSpecTarget, type NativeSceneSpec, type SceneGenerationSemantics, type SceneGenSpec } from '../../protocol/source-scene-generation';
import { createSceneSpecFieldConverter, sceneSpecFormFromSpec, type SceneSpecFormValue } from '../../../../src/lib/agent/scene-gen-spec-core';
import type { SceneSpecDurationInput } from '../../../../src/lib/agent/scene-spec-duration';
import { hasPendingTimelineEdit } from './edit-ownership';
export type NativeSceneSpecHelpers = Pick<SceneGenerationSemantics, 'preserveSceneGenSpec' | 'sceneSpecDurationBounds' | 'sceneSpecDurationBlocked'> & {
    baselineStampSceneGenSpec(spec: SceneGenSpec | undefined): SceneGenSpec | undefined;
    splitSceneGenSpecForPiece(raw: unknown): SceneGenSpec | undefined;
};
export function readSourceSceneSpec(document: unknown, target: SceneSpecTarget, helpers: NativeSceneSpecHelpers): NativeSceneSpec {
    const resolved = resolveSourceSceneSpecOwner(readSceneGraph(document), target.itemId, helpers);
    if (resolved.owner.id !== target.ownerItemId || resolved.meta.sceneId !== target.sceneId) throw Error('씬 설정 소유자가 바뀌었습니다. 다시 선택해 주세요.');
    return resolved.meta;
}
/** One native Undo transaction; zero execution callbacks. Invalid duration leaves other edits saveable. */
export async function planSourceSceneSpecSave(state: EditorState, target: SceneSpecTarget, authority: {
    scope: NativeWorkScope;
    revision: string;
}, form: SceneSpecFormValue, helpers: NativeSceneSpecHelpers, context?: SceneSpecDurationInput): Promise<{
    state: EditorState;
    durationIssue: string | null;
    droppedKeys: string[];
}> {
    if (hasPendingTimelineEdit(state))
        throw Error('이동이나 시간 입력을 마친 뒤 설정을 저장해 주세요.');
    await validateSceneSpecTarget(target, state.undoableState, authority.scope, authority.revision);
    const saved = readSourceSceneSpec(state.undoableState, target, helpers), bounds = helpers.sceneSpecDurationBounds(context ?? saved.durationContext ?? {});
    const invalid = form.durationSec !== null && (!Number.isFinite(form.durationSec) || form.durationSec <= 0);
    const durationIssue = invalid ? '길이는 양수로 입력하거나 자동으로 바꿔 주세요.' : helpers.sceneSpecDurationBlocked(form.durationSec ?? undefined, bounds);
    const safe = durationIssue ? { ...form, durationSec: sceneSpecFormFromSpec(saved.capsule).durationSec } : form;
    const toSpec = createSceneSpecFieldConverter(helpers.preserveSceneGenSpec), raw = toSpec(safe, helpers.baselineStampSceneGenSpec(saved.capsule));
    const preserved = helpers.preserveSceneGenSpec(raw), next: NativeSceneSpec = { ...saved, ...(context ? { durationContext: context } : {}), capsule: preserved.spec };
    if (!next.capsule)
        delete next.capsule;
    const planned = writeSourceSceneSpecState(state, target, next, helpers);
    return { state: editorValueSignature(planned.undoableState) === editorValueSignature(state.undoableState) ? state : planned,
        durationIssue, droppedKeys: preserved.droppedKeys };
}
/** Synchronize only proven identical settings aliases in the same logical scene.
 * Call after target validation; the returned state is one native CAS/Undo transaction. */
export function writeSourceSceneSpecState(state: EditorState, target: SceneSpecTarget, next: NativeSceneSpec,
    helpers?: Pick<NativeSceneSpecHelpers, 'preserveSceneGenSpec'>): EditorState {
    const resolved = resolveSourceSceneSpecOwner(readSceneGraph(state.undoableState), target.itemId, helpers);
    if (resolved.owner.id !== target.ownerItemId || next.ownerItemId !== target.ownerItemId || next.sceneId !== target.sceneId)
        throw Error('씬 설정 소유자가 바뀌었습니다. 다시 선택해 주세요.');
    const ids = new Set([...resolved.copies.map(item => String(item.id)), target.ownerItemId]);
    const items = { ...state.undoableState.items };
    for (const id of ids) {
        const item = items[id]!;
        const edited: typeof item & { sourceSceneSpec: NativeSceneSpec } = { ...item, sourceSceneSpec: next };
        items[id] = edited;
    }
    return { ...state, undoableState: { ...state.undoableState, items } };
}
/** Timeline clip cuts retain scene identity/capsule. Story-scene split must assign new owners/scene IDs.
 * Source story splitting rejects clip scenes; timeline cutting remains available. E owns that command.
 */
export function sourceSceneSpecForSplit(meta: NativeSceneSpec, policy: 'timeline-clip' | 'story-scene', ownerItemId: string, sceneId: string, helpers: Pick<NativeSceneSpecHelpers, 'splitSceneGenSpecForPiece'>): NativeSceneSpec {
    if (policy === 'timeline-clip')
        return { ...meta };
    const capsule = helpers.splitSceneGenSpecForPiece(meta.capsule);
    const next={...meta,ownerItemId,sceneId};if(capsule)next.capsule=capsule;else delete next.capsule;return next;
}
export { sceneSpecFormFromSpec };
